import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental'
import { type Query, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query'
import { humanize, underscore } from 'inflection'
import { EmbedSessionExpiredError, isEmbedMode } from 'v2/shared/utils/embed-session'
import { logger } from 'v2/utils/logger'
import { shouldRetryQuery } from './query-retry'

const queryErrorHandler = (error: Error, query: Query<unknown, unknown, unknown, QueryKey>) => {
  const { queryKey, meta } = query
  // createService reported it already, at the level its reason calls for.
  if (meta?.silent || error instanceof EmbedSessionExpiredError) return
  const queryName = Array.isArray(queryKey) && queryKey.length > 0 ? queryKey[0] : ''
  const humanizedQueryKey = humanize(underscore(queryName), true)
  // errorToast({
  //   title: 'Error',
  //   description: `Failed to load ${humanizedQueryKey} data`,
  // })
  logger.error(`Failed to load ${humanizedQueryKey} data`, error)
}

// As suggested by https://tanstack.com/query/latest/docs/framework/react/plugins/createAsyncStoragePersister
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: convertToMs(1, TimeUnits.Minutes),
      gcTime: convertToMs(1, TimeUnits.Days),
      // Deterministic failures (400, 403, 404) are not retried; everything else keeps the
      // default 3 retries. A client default (not per-query) so tests and local
      // QueryClients keep their own retry policy. The predicate lives in a leaf module
      // so it is testable without importing this one (see ./query-retry).
      retry: shouldRetryQuery,
    },
  },
  queryCache: new QueryCache({
    onError: queryErrorHandler,
  }),
})

// To broadcast the queryClient to other tabs. Never in embed: the channel is shared
// by every same-origin frame, so two embed iframes would replicate each other's cache.
if (!isEmbedMode()) {
  broadcastQueryClient({ queryClient })
}
